import { useId, useRef, type ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { useDialog } from '../../hooks/useDialog'
import { IconButton } from './IconButton'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Buttons, right-aligned on desktop and stacked on mobile. */
  footer?: ReactNode
  /** Material Symbols glyph shown above the title, as the cancel flow does. */
  icon?: string
  className?: string
}

/** Centred confirm dialog. Sheet handles the mobile-first cases; a confirm is
 *  short enough to stay centred at every width. */
export function Modal({ open, onClose, title, description, children, footer, icon, className }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  useDialog(open, onClose, panelRef)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-margin-mobile md:p-gutter">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-inverse-surface/40 backdrop-blur-sm"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 w-full max-w-lg rounded-xl border border-surface-variant bg-surface-container-lowest p-8 outline-none md:p-12',
          className,
        )}
      >
        <div className="absolute right-4 top-4">
          <IconButton icon="close" label="Close" onClick={onClose} size="sm" />
        </div>

        <div className="text-center">
          {icon && <span className="material-symbols-outlined mb-4 text-[32px] text-outline-variant">{icon}</span>}
          <h2 id={titleId} className="font-display text-headline-sm text-on-background">
            {title}
          </h2>
          {description && (
            <p id={descId} className="mt-3 font-body-md text-body-md text-on-surface-variant">
              {description}
            </p>
          )}
        </div>

        {children && <div className="mt-8">{children}</div>}

        {footer && <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">{footer}</div>}
      </div>
    </div>
  )
}
